import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { BrowserRouter, Routes, Route, Outlet, Link, useLocation } from 'react-router-dom'
import { content, DAPP_URL, getContent, type Locale } from './content'
import { AboutPage } from './pages/About'
import { ContactPage } from './pages/Contact'
import { MarketplacePage } from './pages/Marketplace'
import { HorseDetailPage } from './pages/HorseDetail'
import './App.css'

const LOCALE_LABELS: { value: Locale; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'zh', label: '中文' },
  { value: 'ko', label: '한국어' },
  { value: 'ja', label: '日本語' },
  { value: 'vi', label: 'Tiếng Việt' },
]

type LocaleContextValue = { locale: Locale; setLocale: (l: Locale) => void; content: typeof content }
const LocaleContext = React.createContext<LocaleContextValue | null>(null)

export function useLocale(): LocaleContextValue {
  const ctx = React.useContext(LocaleContext)
  if (!ctx) return { locale: 'en', setLocale: () => {}, content }
  return ctx
}

const NAV_ROUTES: { path: string; labelKey: string; hash?: string }[] = [
  { path: '/', labelKey: 'Home' },
  { path: '/marketplace', labelKey: 'Marketplace' },
  { path: '/about', labelKey: 'About' },
  { path: '/', labelKey: 'HowItWorks', hash: '#how-it-works' },
  { path: '/contact', labelKey: 'Contact' },
]

function Nav() {
  const { content: c, locale, setLocale } = useLocale()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [langOpen, setLangOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const navLabels = ((c.navItems as unknown) as { label: string }[] | undefined) ?? [
    { label: '首页' },
    { label: '马匹市场' },
    { label: '关于我们' },
    { label: '运作方式' },
    { label: '联系我们' },
  ]
  const labelMap: Record<string, string> = { Home: navLabels[0]?.label ?? 'Home', Marketplace: navLabels[1]?.label ?? 'Marketplace', About: navLabels[2]?.label ?? 'About', HowItWorks: navLabels[3]?.label ?? 'How It Works', Contact: navLabels[4]?.label ?? 'Contact' }

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`} id="navbar">
      <div className="container">
        <div className="nav-wrapper">
          <Link to="/" className="logo" onClick={() => setMenuOpen(false)}>
            <img src="/images/logo.png" alt="Equine RWA" className="logo-img" />
            <span>Equine RWA</span>
          </Link>
          {/* 移动端顶栏：地球图标（红色圈位置，仅小屏显示） */}
          <div className="nav-mobile-globe">
            <div className="landing-lang-wrap" style={{ position: 'relative' }}>
              <button
                type="button"
                className="btn btn-secondary nav-globe-btn"
                onClick={() => setLangOpen((o) => !o)}
                aria-label="Language"
                title={LOCALE_LABELS.find((l) => l.value === locale)?.label ?? locale}
              >
                <i className="fas fa-globe"></i>
              </button>
              {langOpen && (
                <>
                  <div className="landing-lang-backdrop" onClick={() => setLangOpen(false)} aria-hidden />
                  <div className="landing-lang-dropdown nav-mobile-globe-dropdown" style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: 'var(--color-white)', boxShadow: 'var(--shadow-xl)', borderRadius: 'var(--radius-md)', padding: 8, zIndex: 1001, minWidth: 140 }}>
                    {LOCALE_LABELS.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => { setLocale(value); setLangOpen(false); }}
                        style={{ display: 'block', width: '100%', padding: '8px 12px', textAlign: 'left', border: 'none', background: locale === value ? 'var(--color-background)' : 'transparent', borderRadius: 4 }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <ul className={`nav-menu ${menuOpen ? 'active' : ''}`} id="navMenu">
            {NAV_ROUTES.map((r, i) => {
              const label = typeof navLabels[i] === 'object' && navLabels[i] && 'label' in navLabels[i] ? (navLabels[i] as { label: string }).label : labelMap[r.labelKey] ?? r.labelKey
              const href = r.hash ? `${r.path}${r.hash}` : r.path
              const isActive = r.path === '/' && !r.hash ? location.pathname === '/' : r.hash ? false : location.pathname === r.path || (r.path === '/marketplace' && location.pathname.startsWith('/horse'))
              return (
                <li key={r.path + (r.hash ?? '')}>
                  <Link
                    to={href}
                    className={isActive ? 'active' : ''}
                    onClick={() => setMenuOpen(false)}
                  >
                    {label}
                  </Link>
                </li>
              )
            })}
            {/* 移动端菜单内仅保留 CTA，地球已移至顶栏 */}
            <li className="nav-mobile-actions-wrap">
              <a href={DAPP_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary" onClick={() => setMenuOpen(false)}>
                {c.ctaButton ?? '进入dapp'}
              </a>
            </li>
          </ul>
          <div className="nav-actions">
            <div className="landing-lang-wrap" style={{ position: 'relative' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setLangOpen((o) => !o)}
                aria-label="Language"
                title={LOCALE_LABELS.find((l) => l.value === locale)?.label ?? locale}
              >
                <i className="fas fa-globe"></i>
              </button>
              {langOpen && (
                <>
                  <div className="landing-lang-backdrop" onClick={() => setLangOpen(false)} aria-hidden />
                  <div className="landing-lang-dropdown" style={{ position: 'absolute', top: 0, left: '100%', marginLeft: 8, background: 'var(--color-white)', boxShadow: 'var(--shadow-xl)', borderRadius: 'var(--radius-md)', padding: 8, zIndex: 1001, minWidth: 140 }}>
                    {LOCALE_LABELS.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => { setLocale(value); setLangOpen(false); }}
                        style={{ display: 'block', width: '100%', padding: '8px 12px', textAlign: 'left', border: 'none', background: locale === value ? 'var(--color-background)' : 'transparent', borderRadius: 4 }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <a href={DAPP_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              {c.ctaButton ?? '进入dapp'}
            </a>
          </div>
          <button
            type="button"
            className={`hamburger ${menuOpen ? 'active' : ''}`}
            id="hamburger"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
    </nav>
  )
}

function Hero() {
  const { content: c } = useLocale()
  const hero = c.heroNew ?? c.hero
  const titleLine1 = 'titleLine1' in hero ? hero.titleLine1 : 'Own Champions'
  const titleLine2 = 'titleLine2' in hero ? (hero as { titleLine2?: string }).titleLine2 : 'One Token at a Time'
  const subtitle = hero.subtitle ?? 'Global equine asset digitization platform · Transparent · Compliant · Win-win'
  const ctaBrowse = 'ctaBrowse' in hero ? (hero as { ctaBrowse?: string }).ctaBrowse : 'Browse Horses'
  const ctaLearn = 'ctaLearn' in hero ? (hero as { ctaLearn?: string }).ctaLearn : 'List Your Horse'

  return (
    <section className="hero">
      <div className="hero-background">
        <div className="hero-video-overlay"></div>
        <div className="hero-bg-image"></div>
      </div>
      <div className="container">
        <div className="hero-content">
          <h1 className="hero-title fade-in">
            {titleLine1}
            <br />
            <span className="highlight">{titleLine2}</span>
          </h1>
          <p className="hero-subtitle fade-in">{subtitle}</p>
          <div className="hero-cta fade-in">
            <a href={DAPP_URL} target="_blank" rel="noopener noreferrer" className="btn btn-large btn-primary">
              <i className="fas fa-search"></i> {ctaBrowse}
            </a>
            <a href="#why-rwa" className="btn btn-large btn-primary">
              <i className="fas fa-info-circle"></i> {ctaLearn}
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

function StatsSection() {
  const { content: c } = useLocale()
  const stats = c.statsNew ?? [
    { target: 125000000, label: 'Platform Assets (USD)' },
    { target: 348, label: 'Premium Horses' },
    { target: 892000, label: 'Monthly Distributions (USD)' },
    { target: 15600, label: 'Global Members' },
  ]

  const [animated, setAnimated] = useState<Record<number, boolean>>({})
  const ref = React.useRef<HTMLElement>(null)
  const didAnimate = React.useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      (entries) => {
        if (didAnimate.current || !entries[0]?.isIntersecting) return
        didAnimate.current = true
        const len = stats.length
        setAnimated(
          Object.fromEntries(Array.from({ length: len }, (_, i) => [i, true]))
        )
      },
      { threshold: 0.5 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [stats.length])

  const formatNum = (n: number) => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  return (
    <section className="stats-section" ref={ref}>
      <div className="container">
        <div className="stats-grid">
          {stats.map((s, i) => (
            <div key={i} className="stat-card">
              <div className="stat-icon">
                <i className={['fas fa-coins', 'fas fa-horse', 'fas fa-trophy', 'fas fa-users'][i]}></i>
              </div>
              <div className="stat-content">
                <h3 className="stat-number" data-target={s.target}>
                  {animated[i] ? formatNum(s.target) : '0'}
                </h3>
                <p className="stat-label">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function HowItWorksSection() {
  const { content: c } = useLocale()
  const how = c.howItWorksNew ?? {
    title: 'Three Steps to Become an Owner',
    subtitle: 'Simple, transparent, secure investment process',
    steps: [
      { number: '01', title: 'Choose a Horse', desc: 'Browse our curated horses, view pedigree, record and health files.' },
      { number: '02', title: 'Purchase Shares', desc: 'Complete purchase via crypto wallet or fiat; hold on-chain ownership.' },
      { number: '03', title: 'Earn Returns', desc: 'When the horse wins or the asset appreciates, earn proportional distributions.' },
    ],
  }
  const icons = ['fa-search', 'fa-wallet', 'fa-chart-line']

  return (
    <section className="how-it-works" id="how-it-works">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">{how.title}</h2>
          <p className="section-subtitle">{how.subtitle}</p>
        </div>
        <div className="steps-grid">
          {how.steps.map((step, i) => (
            <React.Fragment key={step.number}>
              <div className="step-card">
                <div className="step-number">{step.number}</div>
                <div className="step-icon">
                  <i className={`fas ${icons[i]}`}></i>
                </div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.desc}</p>
              </div>
              {i < how.steps.length - 1 && (
                <div className="step-arrow">
                  <i className="fas fa-arrow-right"></i>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  )
}

function FeaturedHorsesSection() {
  const { content: c } = useLocale()
  const title = c.featuredHorsesTitle ?? 'Featured Horses'
  const subtitle = c.featuredHorsesSubtitle ?? 'Invest in premium equine assets, share champion glory'
  const comingSoon = (c as { comingSoon?: string }).comingSoon ?? 'Coming Soon'

  return (
    <section className="featured-horses">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">{title}</h2>
          <p className="section-subtitle">{subtitle}</p>
        </div>
        <div className="featured-coming-soon">
          <i className="fas fa-clock"></i>
          <p className="featured-coming-soon-text">{comingSoon}</p>
        </div>
      </div>
    </section>
  )
}

function WhyRwaSection() {
  const { content: c } = useLocale()
  const { titleEn, title, blocks } = c.whyEquineRwa
  const icons = ['fa-certificate', 'fa-shield-alt', 'fa-chart-bar', 'fa-building', 'fa-history', 'fa-water']

  return (
    <section className="why-rwa" id="why-rwa">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">{titleEn ?? title}</h2>
          <p className="section-subtitle">{c.whyRwaSubtitle ?? 'Redefining asset ownership'}</p>
        </div>
        <div className="features-grid">
          {blocks.slice(0, 6).map((block, i) => (
            <div key={block.index} className="feature-card">
              <div className="feature-icon">
                <i className={`fas ${icons[i]}`}></i>
              </div>
              <h3 className="feature-title">{block.titleCn}</h3>
              <p className="feature-description">{block.bodyCn}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function TrustSection() {
  const { content: c } = useLocale()
  const { title, items } = c.assetIntegrity
  const subtitle = c.trustSubtitle ?? 'Transparent, compliant legal architecture'

  return (
    <section className="trust-section">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">{title}</h2>
          <p className="section-subtitle">{subtitle}</p>
        </div>
        <div className="trust-grid">
          {items.map((item, i) => (
            <div key={i} className="trust-item">
              <div className="trust-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <div className="trust-item-content">
                <h4>{item.title}</h4>
                <p>{item.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

type TestimonialItem = { text: string; name: string; title: string; returnPct: string; tagline?: string }
const TESTIMONIALS_FALLBACK: TestimonialItem[] = [
  { text: 'As a tech professional, I\'ve always been interested in blockchain and real assets. Equine RWA let me participate in racehorse investment at an affordable level. My Thunder Strike share paid out after its first win!', name: 'Zhang Ming', title: 'Software Engineer · 3 horse shares', returnPct: '+24%' },
  { text: 'I\'ve loved horses since I was a child but never thought I could be an owner. Through this platform I bought shares in Golden Legacy — now I can watch training and races live. Amazing!', name: 'Li Xinyi', title: 'Marketing Director · 2 horse shares', returnPct: '+18%' },
  { text: 'I\'ve been involved in the racing industry for years, but traditional ownership can be capital-intensive and opaque. Equine RWA introduced a clearer structure for participation. I appreciate being able to follow performance updates while knowing the operational decisions remain with the professionals.', name: 'Chen Wei', title: 'Horse Enthusiast · 4 horse shares', returnPct: '+24%', tagline: 'Participation based on race performance' },
  { text: 'What attracted me to Equine RWA wasn\'t returns, but structure. The platform helped me understand how co-ownership works without having to manage training, logistics, or daily operations. It feels like a more disciplined way to be involved.', name: 'David Lee', title: 'Private Investor · 2 horse shares', returnPct: '+18%', tagline: 'Long-term participation' },
  { text: 'As a long-time follower of thoroughbred racing, I value transparency. Through Equine RWA, I can track ownership details, race participation, and asset updates in one place. It\'s not about speculation — it\'s about informed participation.', name: 'Sophia Tan', title: 'Family Office Associate · 5 horse shares', returnPct: '+22%', tagline: 'Asset-based participation' },
]

function TestimonialsSection() {
  const { content: c } = useLocale()
  const gridRef = React.useRef<HTMLDivElement>(null)
  const title = c.testimonialsTitle ?? '参与者故事'
  const subtitle = c.testimonialsSubtitle ?? '真实参与体验分享'
  const testimonialsList = ((c as unknown as { testimonialsList?: TestimonialItem[] }).testimonialsList ?? TESTIMONIALS_FALLBACK) as TestimonialItem[]
  const annualizedLabel = (c as { testimonialsAnnualized?: string }).testimonialsAnnualized ?? '年化'

  const scroll = (dir: 'left' | 'right') => {
    const el = gridRef.current
    if (!el) return
    const step = el.offsetWidth * 0.6
    el.scrollBy({ left: dir === 'left' ? -step : step, behavior: 'smooth' })
  }

  return (
    <section className="testimonials">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">{title}</h2>
          <p className="section-subtitle">{subtitle}</p>
        </div>
        <div className="testimonials-carousel-wrap">
          <button type="button" className="testimonials-nav testimonials-nav-prev" onClick={() => scroll('left')} aria-label="Previous">
            <i className="fas fa-chevron-left"></i>
          </button>
          <div className="testimonials-grid" ref={gridRef}>
            {testimonialsList.map((t, i) => (
              <div key={i} className="testimonial-card">
                <div className="testimonial-content">
                  <div className="quote-icon">
                    <i className="fas fa-quote-left"></i>
                  </div>
                  <p className="testimonial-text">"{t.text}"</p>
                </div>
                <div className="testimonial-author">
                  <div className="author-avatar" aria-hidden>
                    {t.name.charAt(0)}
                  </div>
                  <div className="author-info">
                    <h4 className="author-name">{t.name}</h4>
                    <p className="author-title">{t.title}</p>
                    <div className="author-return">
                      {'tagline' in t && t.tagline ? t.tagline : <><i className="fas fa-arrow-up"></i> {t.returnPct} {annualizedLabel}</>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button type="button" className="testimonials-nav testimonials-nav-next" onClick={() => scroll('right')} aria-label="Next">
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </section>
  )
}

const PARTNERS_FALLBACK = [
  { icon: 'fa-university', name: 'Global Equine Federation' },
  { icon: 'fa-shield-alt', name: 'International Insurance' },
  { icon: 'fa-briefcase', name: 'Blockchain Custody' },
  { icon: 'fa-gavel', name: 'Law Firms' },
  { icon: 'fa-hospital', name: 'Veterinary Certification' },
  { icon: 'fa-newspaper', name: 'Equine Media' },
]

function PartnersSection() {
  const { content: c } = useLocale()
  const title = c.partnersTitle ?? 'Partners'
  const subtitle = c.partnersSubtitle ?? 'Trusted platform with top institutions'
  const list = ((c as unknown) as { partnersList?: readonly { icon: string; name: string }[] }).partnersList ?? PARTNERS_FALLBACK

  return (
    <section className="partners">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">{title}</h2>
          <p className="section-subtitle">{subtitle}</p>
        </div>
        <div className="partners-grid">
          {list.map((p, i) => (
            <div key={i} className="partner-logo">
              <i className={`fas ${p.icon}`}></i>
              <p>{p.name}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

const FAQ_ITEMS = [
  { q: 'What is Equine RWA?', a: 'Equine RWA is a platform that tokenizes real racehorse assets. Through blockchain, we split economic rights into tradable tokens so more people can participate with lower barriers. Each token is backed by real horses with full pedigree, health and insurance records.' },
  { q: 'How much do I need to start?', a: 'Minimum investment varies by horse; typically from $1,000 to $5,000 per share. You can choose according to your budget. We support crypto and fiat payment.' },
  { q: 'How are returns calculated and distributed?', a: 'Returns come from race prizes, breeding fees and asset appreciation. All distributions are sent to your wallet automatically by share proportion. Everything is transparent and verifiable on-chain. We do not promise fixed returns; all outcomes are performance-based.' },
  { q: 'What are the risks?', a: 'Main risks: 1) Race performance (horse may lose); 2) Health (injuries, though covered by insurance); 3) Liquidity (secondary market may be limited); 4) Regulation (laws may change). We recommend reading our risk disclosure and diversifying.' },
  { q: 'How do you ensure compliance and security?', a: 'We work with top law firms and have a clear legal structure (SPV/trust). All horses are held by licensed custodians with full veterinary and insurance records. The platform is audited and all transactions are on-chain and transparent. We comply with local regulations and require KYC.' },
  { q: 'Can I sell my shares?', a: 'Yes, you can sell on our secondary market. Liquidity may vary by horse and demand. We are working with exchanges to improve liquidity.' },
]

function FaqSection() {
  const { content: c } = useLocale()
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const title = c.faqTitle ?? 'FAQ'
  const subtitle = c.faqSubtitle ?? 'Answers to your questions'
  const items = c.faqItems ?? FAQ_ITEMS

  return (
    <section className="faq">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">{title}</h2>
          <p className="section-subtitle">{subtitle}</p>
        </div>
        <div className="faq-container">
          {items.map((item, i) => (
            <div key={i} className={`faq-item ${openIndex === i ? 'active' : ''}`}>
              <button
                type="button"
                className="faq-question"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span>{item.q}</span>
                <i className="fas fa-chevron-down"></i>
              </button>
              <div className="faq-answer">
                <p>{item.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function CtaSection() {
  const { content: c } = useLocale()
  const cta = c.ctaNew ?? {
    title: 'Ready to become an owner?',
    subtitle: 'Explore featured horses and start your investment journey',
    primary: 'Start Investing Now',
    secondary: 'Contact Us',
  }

  return (
    <section className="cta-section" id="cta-section">
      <div className="container">
        <div className="cta-content">
          <h2 className="cta-title">{cta.title}</h2>
          <p className="cta-subtitle">{cta.subtitle}</p>
          <div className="cta-buttons">
            <a href={DAPP_URL} target="_blank" rel="noopener noreferrer" className="btn btn-large btn-primary">
              <i className="fas fa-rocket"></i> {cta.primary}
            </a>
            <Link to="/contact" className="btn btn-large btn-secondary">
              <i className="fas fa-comments"></i> {cta.secondary}
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

function Footer() {
  const { content: c } = useLocale()
  const footer = c.footer
  const desc = footer.description ?? 'Global equine asset digitization platform. Everyone can own champion racehorses.'

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-col">
            <div className="footer-logo">
              <img src="/images/logo.png" alt="Equine RWA" className="logo-img" />
              <span>Equine RWA</span>
            </div>
            <p className="footer-description">{desc}</p>
            <div className="footer-social">
              <a href="#" aria-label="Twitter"><i className="fab fa-twitter"></i></a>
              <a href="#" aria-label="Telegram"><i className="fab fa-telegram"></i></a>
              <a href="#" aria-label="Discord"><i className="fab fa-discord"></i></a>
              <a href="#" aria-label="Medium"><i className="fab fa-medium"></i></a>
            </div>
          </div>
          <div className="footer-col">
            <h4 className="footer-title">{c.footerPlatformTitle ?? 'Platform'}</h4>
            <ul className="footer-links">
              <li><Link to="/marketplace">{((c.navItems as unknown) as { label: string }[])?.[1]?.label ?? 'Marketplace'}</Link></li>
              <li><Link to="/#how-it-works">{((c.navItems as unknown) as { label: string }[])?.[3]?.label ?? 'How It Works'}</Link></li>
              <li><Link to="/about">{((c.navItems as unknown) as { label: string }[])?.[2]?.label ?? 'About'}</Link></li>
              <li><a href="#">Whitepaper</a></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4 className="footer-title">{c.footerResourcesTitle ?? 'Resources'}</h4>
            <ul className="footer-links">
              <li><a href="#">Help</a></li>
              <li><a href="#">Risk Disclosure</a></li>
              <li><a href="#">Legal</a></li>
              <li><a href="#">API</a></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4 className="footer-title">{c.footerContactTitle ?? 'Contact'}</h4>
            <ul className="footer-links">
              <li><Link to="/contact">{((c.navItems as unknown) as { label: string }[])?.[4]?.label ?? 'Contact'}</Link></li>
              <li><a href="#">Partnership</a></li>
              <li><a href="#">Media</a></li>
              <li><a href="#">Careers</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 Equine RWA. All rights reserved.</p>
          <div className="footer-legal">
            <a href="#">Privacy</a>
            <a href="#">Terms</a>
            <a href="#">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  )
}

function BackToTop() {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  const scrollTop = useCallback(() => window.scrollTo({ top: 0, behavior: 'smooth' }), [])

  if (!visible) return null
  return (
    <button type="button" className="back-to-top visible" id="backToTop" onClick={scrollTop} aria-label="Back to top">
      <i className="fas fa-chevron-up"></i>
    </button>
  )
}

function HomePage() {
  return (
    <>
      <Hero />
      <StatsSection />
      <HowItWorksSection />
      <FeaturedHorsesSection />
      <WhyRwaSection />
      <TrustSection />
      <TestimonialsSection />
      <PartnersSection />
      <FaqSection />
      <CtaSection />
    </>
  )
}

function Layout() {
  return (
    <>
      <Nav />
      <main>
        <Outlet />
      </main>
      <Footer />
      <BackToTop />
    </>
  )
}

export default function App() {
  const [locale, setLocale] = useState<Locale>('en')
  const localeContent = useMemo(() => getContent(locale), [locale])

  return (
    <LocaleContext.Provider value={{ locale, setLocale, content: localeContent }}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="marketplace" element={<MarketplacePage />} />
            <Route path="horse/:id" element={<HorseDetailPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="contact" element={<ContactPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </LocaleContext.Provider>
  )
}
